import { useMemo } from 'react';
import { Transaction } from '@/types/transaction';
import { fromDateString } from '@/lib/utils/dateUtils';

interface CategoryAnalytics {
  name: string;
  totalAmount: number;
  transactionCount: number;
  percentage: number;
  type: 'income' | 'expense';
}

interface MonthlyComparison {
  currentMonth: number;
  previousMonth: number;
  change: number;
  changePercentage: number;
}

export function useDashboardAnalytics(transactions: Transaction[] | undefined) {
  return useMemo(() => {
    if (!transactions || transactions.length === 0) {
      return {
        categoryAnalytics: [],
        monthlyComparison: null,
        topIncomeCategories: [],
        topExpenseCategories: []
      };
    }

    // Analytics por categoria
    const categoryMap = new Map<string, { amount: number; count: number; type: 'income' | 'expense' }>();

    transactions.forEach(transaction => {
      // Prioritize the flattened category name string, then try the relation object, then fallback
      const categoryKey = transaction.category || transaction.categories?.name || 'Sem Categoria';
      const existing = categoryMap.get(categoryKey) || { amount: 0, count: 0, type: transaction.type };

      categoryMap.set(categoryKey, {
        amount: existing.amount + Number(transaction.amount),
        count: existing.count + 1,
        type: transaction.type
      });
    });

    const totalAmount = transactions.reduce((sum, t) => sum + Number(t.amount), 0);

    const categoryAnalytics: CategoryAnalytics[] = Array.from(categoryMap.entries())
      .map(([name, data]) => ({
        name,
        totalAmount: data.amount,
        transactionCount: data.count,
        percentage: (data.amount / totalAmount) * 100,
        type: data.type
      }))
      .sort((a, b) => b.totalAmount - a.totalAmount);

    // Separação por tipo
    const topIncomeCategories = categoryAnalytics
      .filter(cat => cat.type === 'income')
      .slice(0, 5);

    const topExpenseCategories = categoryAnalytics
      .filter(cat => cat.type === 'expense')
      .slice(0, 5);

    // Comparação mensal
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    const previousMonth = currentMonth === 0 ? 11 : currentMonth - 1;
    const previousYear = currentMonth === 0 ? currentYear - 1 : currentYear;

    const currentMonthTransactions = transactions.filter(t => {
      const date = t.date instanceof Date ? t.date : fromDateString(t.date);
      return date.getMonth() === currentMonth && date.getFullYear() === currentYear;
    });

    const previousMonthTransactions = transactions.filter(t => {
      const date = t.date instanceof Date ? t.date : fromDateString(t.date);
      return date.getMonth() === previousMonth && date.getFullYear() === previousYear;
    });

    const currentMonthTotal = currentMonthTransactions.reduce((sum, t) => sum + Number(t.amount), 0);
    const previousMonthTotal = previousMonthTransactions.reduce((sum, t) => sum + Number(t.amount), 0);

    const change = currentMonthTotal - previousMonthTotal;
    const changePercentage = previousMonthTotal > 0 ? (change / previousMonthTotal) * 100 : 0;

    const monthlyComparison: MonthlyComparison = {
      currentMonth: currentMonthTotal,
      previousMonth: previousMonthTotal,
      change,
      changePercentage
    };

    return {
      categoryAnalytics,
      monthlyComparison,
      topIncomeCategories,
      topExpenseCategories
    };
  }, [transactions]);
}